import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { AppStateService } from '@app/services/app-state.service';
import { AppInitState, NeedLoginError } from '@app/models/app-init-state.model';
import { NeoConfigAccessLevel } from '@app/models/neo-config';

/**
 * Role Guard
 *
 * Protects routes that require specific roles (Admin or SuperAdmin).
 * Should be used AFTER AuthGuard has verified authentication.
 * Uses AppStateService to ensure the state machine has completed.
 */
@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate, CanActivateChild {
  private authService = inject(AuthService);
  private appStateService = inject(AppStateService);
  private router = inject(Router);

  private readonly adminRoles = [
    NeoConfigAccessLevel.NeoAdmin,
    NeoConfigAccessLevel.NeoSuperAdmin
  ];

  async canActivate(): Promise<boolean | UrlTree> {
    console.log('🛡️ [ROLE GUARD] canActivate() called');
    return this.ensureAuthorized();
  }

  async canActivateChild(): Promise<boolean | UrlTree> {
    console.log('🛡️ [ROLE GUARD] canActivateChild() called');
    return this.ensureAuthorized();
  }

  private async ensureAuthorized(): Promise<boolean | UrlTree> {
    console.log('🛡️ [ROLE GUARD] ensureAuthorized() - Checking authorization...');

    // Ensure app state machine has completed
    const currentState = this.appStateService.currentState();
    if (currentState !== AppInitState.Ready) {
      console.log('🛡️ [ROLE GUARD] App not ready, waiting for initialization...');
      try {
        await this.appStateService.initialize();
      } catch (error: any) {
        // NeedLoginError means login redirect is being triggered
        if (error instanceof NeedLoginError || error?.name === 'NeedLoginError') {
          console.log('🛡️ [ROLE GUARD] Login redirect triggered by AppStateService');
          // Return false to let the redirect happen
          return false;
        }

        console.error('🛡️ [ROLE GUARD] ❌ App initialization failed:', error);
        return this.router.parseUrl('/');
      }
    }

    const isAuthenticated = this.authService.isAuthenticated();
    console.log('🛡️ [ROLE GUARD] isAuthenticated:', isAuthenticated);

    if (!isAuthenticated) {
      console.log('🛡️ [ROLE GUARD] ❌ User not authenticated, redirecting to home');
      return this.router.parseUrl('/');
    }

    const user = this.authService.user();
    console.log('🛡️ [ROLE GUARD] Current user:', user?.email);
    console.log('🛡️ [ROLE GUARD] Required admin roles:', this.adminRoles);

    const hasAdminRole = this.authService.hasAnyRole(this.adminRoles);
    console.log('🛡️ [ROLE GUARD] hasAdminRole:', hasAdminRole);

    if (!hasAdminRole) {
      console.log('🛡️ [ROLE GUARD] ❌ User lacks required admin role, redirecting to /unauthorized');
      sessionStorage.removeItem('postLoginRedirectPath');
      return this.router.createUrlTree(['/unauthorized'], {
        queryParams: { reason: 'insufficient_roles' }
      });
    }

    console.log('🛡️ [ROLE GUARD] ✅ Authorization successful, allowing access');
    return true;
  }
}
